import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, SlidersHorizontal, Bed, Bath, Car, DollarSign, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { supabase } from '../../lib/supabase';
import { MultiSelectDropdown } from './MultiSelectDropdown';
import { useAuth } from '../../contexts/AuthContext';

const formatWithCommas = (value: string | number | undefined) => {
    if (value === undefined || value === '') return '';
    const stringValue = value.toString().replace(/,/g, '');
    const parts = stringValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join('.');
};

const parseCommas = (value: string) => {
    return value.replace(/,/g, '');
};

export interface AdvancedFilters {
    operation?: 'All' | 'sale' | 'rent';
    propertyStatus?: 'All' | 'available' | 'reserved' | 'sold' | 'rented' | 'off_market';
    propertyType?: string[];
    minPrice?: string;
    maxPrice?: string;
    bedrooms?: number[];
    minBaths?: number;
    minParking?: number;
    publishStatus?: 'All' | 'published' | 'unpublished' | 'expired';
    // 'expiring' = shared/expired in the network and lapsing within
    // SHARE_RENEWAL_WINDOW_DAYS (src/lib/share-renewal.ts), not a share_status value.
    shareStatus?: 'All' | 'shared' | 'not_shared' | 'expiring';
    buildUpSizeMin?: string;
    buildUpSizeMax?: string;
    landSizeMin?: string;
    landSizeMax?: string;
    amenities?: string[];
    department_id?: number;
    city_ids?: number[];
    sector_ids?: number[];
    agent_ids?: string[];
    // Projects (proyectos) the agency created; matches properties.proyecto_id.
    proyecto_ids?: string[];
    // Quick chip filter — true means "only show featured properties".
    // No client-facing toggle in the modal; set/cleared by the chip row.
    is_featured?: boolean;
}

type LocationOption = { id: number; name: string };
type SectorOption = LocationOption & { city_id: number };

interface PropertyFiltersModalProps {
    isOpen: boolean;
    onClose: () => void;
    theme: 'light' | 'dark' | 'high-contrast';
    currentFilters: AdvancedFilters;
    onApplyFilters: (filters: AdvancedFilters) => void;
    // Hide the per-agent picker for users who can only see their own properties
    // (agents without the "Ver todas las propiedades" permission, or scoped to
    // "Mías"). Defaults to shown for admins / permission-holders.
    showAgentFilter?: boolean;
}

export const PropertyFiltersModal: React.FC<PropertyFiltersModalProps> = ({ isOpen, onClose, theme, currentFilters, onApplyFilters, showAgentFilter = true }) => {
    const isLight = theme !== 'dark';
    const [filters, setFilters] = useState<AdvancedFilters>(currentFilters);
    const [shouldRender, setShouldRender] = useState(isOpen);
    const [isClosing, setIsClosing] = useState(false);
    const [dbPropertyTypes, setDbPropertyTypes] = useState<string[]>([]);
    const [dbAmenities, setDbAmenities] = useState<string[]>([]);
    const [departments, setDepartments] = useState<LocationOption[]>([]);
    const [cities, setCities] = useState<LocationOption[]>([]);
    const [sectors, setSectors] = useState<SectorOption[]>([]);
    const [agents, setAgents] = useState<{ id: string; name: string }[]>([]);
    const [proyectos, setProyectos] = useState<{ id: string; name: string }[]>([]);
    const { profile } = useAuth();
    const agencyId = profile?.agency_id;

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsClosing(false);
            setFilters(currentFilters);
        } else if (shouldRender) {
            setIsClosing(true);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsClosing(false);
            }, 400); // Wait for CSS animation to finish
            return () => clearTimeout(timer);
        }
    }, [isOpen, currentFilters, shouldRender]);

    useEffect(() => {
        if (!supabase) return;
        const fetchFilterData = async () => {
            const [ptRes, amRes, deptRes] = await Promise.all([
                supabase.from('property_types').select('name').eq('is_active', true).order('name'),
                supabase.from('amenities').select('name').order('name'),
                supabase.from('departments').select('id, name').eq('is_active', true).order('sort_order').order('name'),
            ]);
            if (ptRes.data) setDbPropertyTypes(ptRes.data.map((p: any) => p.name));
            if (amRes.data && amRes.data.length > 0) {
                setDbAmenities(amRes.data.map((a: any) => a.name));
            } else {
                setDbAmenities(['Piscina', 'Gimnasio', 'Seguridad 24/7', 'Parqueo de Visitas', 'Jardín', 'Terraza', 'Balcón', 'Área Social', 'Pet Friendly', 'Canchas Deportivas', 'Coworking', 'Juegos Infantiles']);
            }
            if (deptRes.data) setDepartments(deptRes.data as LocationOption[]);
        };
        fetchFilterData();
    }, []);

    // Agents in the user's agency. Scoped client-side for clarity even though
    // RLS would also restrict — keeps the picker honest for super admins (who
    // can read across agencies) and impersonators alike.
    useEffect(() => {
        if (!supabase || !agencyId) return;
        let cancelled = false;
        supabase
            .from('profiles')
            .select('id, full_name')
            .eq('agency_id', agencyId)
            .eq('status', 'active')
            .order('full_name')
            .then(({ data }) => {
                if (cancelled || !data) return;
                setAgents(data.map((p: any) => ({ id: p.id as string, name: p.full_name as string })));
            });
        return () => { cancelled = true; };
    }, [agencyId]);

    // Proyectos in the current user's agency — the same set the property form
    // assigns from. Powers the Proyecto filter below.
    useEffect(() => {
        if (!supabase || !agencyId) return;
        let cancelled = false;
        supabase
            .from('proyectos')
            .select('id, nombre')
            .eq('agency_id', agencyId)
            .eq('is_deleted', false)
            .order('nombre')
            .then(({ data }) => {
                if (cancelled || !data) return;
                setProyectos(data.map((p: any) => ({ id: p.id as string, name: p.nombre as string })));
            });
        return () => { cancelled = true; };
    }, [agencyId]);

    // Cascading: load Sectors (cities table) when Departamento changes.
    useEffect(() => {
        if (!supabase || !filters.department_id) {
            setCities([]);
            return;
        }
        let cancelled = false;
        supabase
            .from('cities')
            .select('id, name')
            .eq('department_id', filters.department_id)
            .eq('is_active', true)
            .order('name')
            .then(({ data }) => {
                if (!cancelled && data) setCities(data as LocationOption[]);
            });
        return () => { cancelled = true; };
    }, [filters.department_id]);

    // Cascading: load Sub-Sectors (sectors table) for every selected Sector
    // (city). With multi-select Sectors the Sub-Sector chip cloud is the union
    // of sectors across all chosen cities.
    const cityIdsKey = (filters.city_ids || []).join(',');
    useEffect(() => {
        if (!supabase || !filters.city_ids || filters.city_ids.length === 0) {
            setSectors([]);
            return;
        }
        let cancelled = false;
        supabase
            .from('sectors')
            .select('id, name, city_id')
            .in('city_id', filters.city_ids)
            .eq('is_active', true)
            .order('name')
            .then(({ data }) => {
                if (cancelled || !data) return;
                const next = data as SectorOption[];
                setSectors(next);
                // Prune any previously-selected sub-sectors that no longer
                // belong to a still-selected sector. Without this, removing a
                // city would leave its dangling sector_ids in the filter and
                // silently zero the grid.
                const validIds = new Set(next.map(s => s.id));
                setFilters(prev => {
                    const current = prev.sector_ids || [];
                    const filtered = current.filter(id => validIds.has(id));
                    return filtered.length === current.length ? prev : { ...prev, sector_ids: filtered };
                });
            });
        return () => { cancelled = true; };
    }, [cityIdsKey]);

    if (!shouldRender) return null;

    const handleApply = () => {
        onApplyFilters(filters);
        onClose();
    };

    const handleReset = () => {
        const defaultFilters: AdvancedFilters = {
            operation: 'All',
            propertyStatus: 'All',
            propertyType: [],
            minPrice: '',
            maxPrice: '',
            bedrooms: [],
            minBaths: 0,
            minParking: 0,
            publishStatus: 'All',
            shareStatus: 'All',
            buildUpSizeMin: '',
            buildUpSizeMax: '',
            landSizeMin: '',
            landSizeMax: '',
            amenities: [],
            department_id: undefined,
            city_ids: [],
            sector_ids: [],
            agent_ids: [],
            proyecto_ids: [],
        };
        setFilters(defaultFilters);
        onApplyFilters(defaultFilters);
        onClose();
    };

    const propertyTypesList = dbPropertyTypes.length > 0
        ? dbPropertyTypes
        : ['Apartamento', 'Bodega', 'Casa', 'Casa Vacacional', 'Edificio', 'Local', 'Oficina', 'Terreno'];

    const toggleBedroom = (beds: number) => {
        setFilters(prev => {
            const current = prev.bedrooms || [];
            if (current.includes(beds)) {
                return { ...prev, bedrooms: current.filter(b => b !== beds) };
            }
            return { ...prev, bedrooms: [...current, beds].sort() };
        });
    };

    const togglePropertyType = (type: string) => {
        setFilters(prev => {
            const current = prev.propertyType || [];
            if (current.includes(type)) {
                return { ...prev, propertyType: current.filter(t => t !== type) };
            }
            return { ...prev, propertyType: [...current, type] };
        });
    };

    const toggleAmenity = (amenity: string) => {
        setFilters(prev => {
            const current = prev.amenities || [];
            if (current.includes(amenity)) {
                return { ...prev, amenities: current.filter(a => a !== amenity) };
            }
            return { ...prev, amenities: [...current, amenity] };
        });
    };

    const modalContent = (
        <div className="fixed inset-0 z-[100] flex justify-end overflow-hidden">
            {/* Backdrop */}
            <div
                className={clsx(
                    "absolute inset-0 bg-slate-950/60 backdrop-blur-sm",
                    isClosing ? "animate-fade-out" : "animate-fade-in"
                )}
                onClick={onClose}
            />

            {/* Panel */}
            <div className={clsx(
                "relative w-full max-w-[440px] h-full shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col",
                isClosing ? "animate-slide-right-out" : "animate-slide-right",
                isLight ? "bg-white" : "bg-slate-900 border-l border-white/5"
            )}>
                {/* Header */}
                <div className={clsx(
                    "flex items-center justify-between px-8 py-6 border-b",
                    isLight ? "border-slate-100" : "border-white/5"
                )}>
                    <div>
                        <h3 className={clsx("font-black text-xl tracking-tight mb-1", isLight ? "text-slate-900" : "text-white")}>
                            Filtros Avanzados
                        </h3>
                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Personaliza tu búsqueda</p>
                    </div>
                    <button
                        onClick={onClose}
                        className={clsx(
                            "p-3 rounded-2xl transition-all active:scale-90",
                            isLight ? "bg-slate-50 text-slate-400 hover:bg-slate-100" : "bg-white/5 text-slate-500 hover:bg-white/10 hover:text-white"
                        )}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 px-8 py-6 custom-scrollbar overflow-y-auto space-y-10">

                    {/* Operation */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Tipo de Operación</label>
                        <div className={clsx(
                            "flex p-1.5 rounded-2xl border",
                            isLight ? "bg-slate-50 border-slate-100" : "bg-white/5 border-white/5"
                        )}>
                            {([
                                { value: 'All', label: 'CUALQUIERA' },
                                { value: 'sale', label: 'VENTA' },
                                { value: 'rent', label: 'ALQUILER' },
                            ] as const).map((op) => (
                                <button
                                    key={op.value}
                                    onClick={() => setFilters(prev => ({ ...prev, operation: op.value }))}
                                    className={clsx(
                                        "flex-1 py-2.5 text-xs font-black rounded-xl transition-all duration-300",
                                        (filters.operation === op.value || (!filters.operation && op.value === 'All'))
                                            ? isLight
                                                ? "bg-white text-indigo-600 shadow-md shadow-slate-200 border border-slate-100"
                                                : "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                                            : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                                    )}
                                >
                                    {op.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Property Status (CRM/transaction state) */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Estado de Propiedad</label>
                        <div className="flex flex-wrap gap-2">
                            {([
                                { value: 'All', label: 'CUALQUIERA' },
                                { value: 'available', label: 'DISPONIBLE' },
                                { value: 'reserved', label: 'RESERVADA' },
                                { value: 'sold', label: 'VENDIDA' },
                                { value: 'rented', label: 'ALQUILADA' },
                                { value: 'off_market', label: 'FUERA DE MERCADO' },
                            ] as const).map((opt) => {
                                const active = (filters.propertyStatus || 'All') === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => setFilters(prev => ({ ...prev, propertyStatus: opt.value }))}
                                        className={clsx(
                                            "px-4 py-2.5 rounded-xl text-[11px] font-black border transition-all duration-300",
                                            active
                                                ? isLight
                                                    ? "bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                                                    : "bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/30"
                                                : isLight
                                                    ? "bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-400"
                                                    : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:border-white/20"
                                        )}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Location (Departamento → Sector → Sub-Sector). UI labels
                        match AddPropertyView: "Sector" maps to cities table,
                        "Sub-Sector" maps to sectors table. Departamento is a
                        single select (the cascade is "all sectors WITHIN one
                        departamento"); Sector and Sub-Sector are multi-select
                        chip clouds. */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Ubicación</label>
                        <div className="space-y-5">
                            <div className="space-y-2">
                                <p className="text-[10px] font-bold text-slate-400 pl-1 uppercase">Departamento</p>
                                <select
                                    value={filters.department_id ?? ''}
                                    onChange={e => {
                                        const v = e.target.value ? Number(e.target.value) : undefined;
                                        setFilters(prev => ({ ...prev, department_id: v, city_ids: [], sector_ids: [] }));
                                    }}
                                    className={clsx(
                                        "w-full px-4 py-3 rounded-2xl border text-xs font-black transition-all appearance-none outline-none cursor-pointer",
                                        isLight ? "bg-slate-50 border-slate-200 focus:bg-white text-slate-950" : "bg-white/5 border-white/5 text-white focus:bg-white/10"
                                    )}
                                >
                                    <option value="">CUALQUIERA</option>
                                    {departments.map(d => (
                                        <option key={d.id} value={d.id}>{d.name.toUpperCase()}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-baseline justify-between pl-1">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase">Sector</p>
                                    {(filters.city_ids?.length ?? 0) > 0 && (
                                        <button
                                            onClick={() => setFilters(prev => ({ ...prev, city_ids: [], sector_ids: [] }))}
                                            className="text-[10px] font-bold text-slate-400 hover:text-rose-500 uppercase"
                                        >
                                            Limpiar
                                        </button>
                                    )}
                                </div>
                                <MultiSelectDropdown
                                    theme={theme}
                                    options={cities}
                                    selectedIds={filters.city_ids ?? []}
                                    onChange={(ids) => setFilters(prev => ({ ...prev, city_ids: ids }))}
                                    placeholder="Selecciona uno o más sectores"
                                    disabled={!filters.department_id}
                                    disabledMessage="Selecciona un departamento primero"
                                    emptyMessage="Cargando…"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-baseline justify-between pl-1">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase">Sub-Sector</p>
                                    {(filters.sector_ids?.length ?? 0) > 0 && (
                                        <button
                                            onClick={() => setFilters(prev => ({ ...prev, sector_ids: [] }))}
                                            className="text-[10px] font-bold text-slate-400 hover:text-rose-500 uppercase"
                                        >
                                            Limpiar
                                        </button>
                                    )}
                                </div>
                                <MultiSelectDropdown
                                    theme={theme}
                                    options={sectors}
                                    selectedIds={filters.sector_ids ?? []}
                                    onChange={(ids) => setFilters(prev => ({ ...prev, sector_ids: ids }))}
                                    placeholder="Selecciona uno o más sub-sectores"
                                    disabled={(filters.city_ids?.length ?? 0) === 0}
                                    disabledMessage="Selecciona uno o más sectores primero"
                                    emptyMessage="Sin sub-sectores disponibles"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Property Type Selection */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Tipo de Propiedad</label>
                        <div className="flex flex-wrap gap-2">
                            {propertyTypesList.map(type => {
                                const isSelected = (filters.propertyType || []).includes(type);
                                return (
                                    <button
                                        key={type}
                                        onClick={() => togglePropertyType(type)}
                                        className={clsx(
                                            "px-4 py-2.5 rounded-xl text-[11px] font-black border transition-all duration-300",
                                            isSelected
                                                ? isLight
                                                    ? "bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                                                    : "bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/30"
                                                : isLight
                                                    ? "bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-400"
                                                    : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:border-white/20"
                                        )}
                                    >
                                        {type.toUpperCase()}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Price Range */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Rango de Inversión (USD)</label>
                        <div className="flex gap-4">
                            <div className={clsx(
                                "flex-1 group relative rounded-2xl border transition-all duration-300",
                                isLight ? "bg-slate-50 border-slate-200 focus-within:bg-white focus-within:ring-4 focus-within:ring-indigo-100 focus-within:border-indigo-300"
                                    : "bg-white/5 border-white/5 focus-within:bg-white/10 focus-within:border-indigo-500/50"
                            )}>
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 font-bold">$</span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="MIN"
                                    value={formatWithCommas(filters.minPrice)}
                                    onChange={e => {
                                        const parsed = parseCommas(e.target.value);
                                        if (/^\d*\.?\d*$/.test(parsed)) {
                                            setFilters(prev => ({ ...prev, minPrice: parsed }));
                                        }
                                    }}
                                    className={clsx(
                                        "bg-transparent border-none py-4 px-10 outline-none w-full text-sm font-black tracking-tight placeholder:text-slate-400",
                                        isLight ? "text-slate-950" : "text-white"
                                    )}
                                />
                            </div>
                            <div className={clsx(
                                "flex-1 group relative rounded-2xl border transition-all duration-300",
                                isLight ? "bg-slate-50 border-slate-200 focus-within:bg-white focus-within:ring-4 focus-within:ring-indigo-100 focus-within:border-indigo-300"
                                    : "bg-white/5 border-white/5 focus-within:bg-white/10 focus-within:border-indigo-500/50"
                            )}>
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 font-bold">$</span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="MAX"
                                    value={formatWithCommas(filters.maxPrice)}
                                    onChange={e => {
                                        const parsed = parseCommas(e.target.value);
                                        if (/^\d*\.?\d*$/.test(parsed)) {
                                            setFilters(prev => ({ ...prev, maxPrice: parsed }));
                                        }
                                    }}
                                    className={clsx(
                                        "bg-transparent border-none py-4 px-10 outline-none w-full text-sm font-black tracking-tight placeholder:text-slate-400",
                                        isLight ? "text-slate-950" : "text-white"
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bedrooms Selection */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Dormitorios</label>
                        <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map(num => {
                                const isSelected = (filters.bedrooms || []).includes(num);
                                return (
                                    <button
                                        key={num}
                                        onClick={() => toggleBedroom(num)}
                                        className={clsx(
                                            "flex-1 py-4 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all duration-300",
                                            isSelected
                                                ? isLight
                                                    ? "bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                                                    : "bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/30"
                                                : isLight
                                                    ? "bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-400"
                                                    : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:border-white/20"
                                        )}
                                    >
                                        <span className="text-[17px] font-black">{num}{num === 5 ? '+' : ''}</span>
                                        <Bed size={12} className={isSelected ? 'text-current opacity-80' : 'opacity-30'} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Additional Spaces (Baths/Parking) */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Espacios & Comodidades</label>
                        <div className="flex gap-4">
                            <div className={clsx(
                                "flex-1 flex flex-col gap-1 px-4 py-3 rounded-2xl border",
                                isLight ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/5"
                            )}>
                                <div className="flex items-center gap-2">
                                    <Bath size={14} className="text-slate-400" />
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-tight">Baños</span>
                                </div>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={filters.minBaths || ''}
                                    onChange={e => setFilters(prev => ({ ...prev, minBaths: Number(e.target.value) }))}
                                    placeholder="0"
                                    className={clsx(
                                        "bg-transparent border-none p-0 w-full outline-none text-[15px] font-black",
                                        isLight ? "text-slate-950" : "text-white"
                                    )}
                                />
                            </div>
                            <div className={clsx(
                                "flex-1 flex flex-col gap-1 px-4 py-3 rounded-2xl border",
                                isLight ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/5"
                            )}>
                                <div className="flex items-center gap-2">
                                    <Car size={14} className="text-slate-400" />
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-tight">Parqueos</span>
                                </div>
                                <input
                                    type="number"
                                    min="0"
                                    value={filters.minParking || ''}
                                    onChange={e => setFilters(prev => ({ ...prev, minParking: Number(e.target.value) }))}
                                    placeholder="0"
                                    className={clsx(
                                        "bg-transparent border-none p-0 w-full outline-none text-[15px] font-black",
                                        isLight ? "text-slate-950" : "text-white"
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Measurements Section */}
                    <div className="grid grid-cols-2 gap-8">
                        <div className="space-y-4">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Construcción</label>
                            <div className="space-y-2">
                                <div className={clsx("relative rounded-2xl border px-4 py-2", isLight ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/5")}>
                                    <span className="text-[9px] font-bold text-slate-400 block mb-0.5">MÍN M²</span>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={formatWithCommas(filters.buildUpSizeMin)}
                                        onChange={e => {
                                            const parsed = parseCommas(e.target.value);
                                            if (/^\d*\.?\d*$/.test(parsed)) {
                                                setFilters(prev => ({ ...prev, buildUpSizeMin: parsed }));
                                            }
                                        }}
                                        className={clsx(
                                            "bg-transparent border-none p-0 w-full outline-none text-sm font-bold",
                                            isLight ? "text-slate-950" : "text-white"
                                        )}
                                    />
                                </div>
                                <div className={clsx("relative rounded-2xl border px-4 py-2", isLight ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/5")}>
                                    <span className="text-[9px] font-bold text-slate-400 block mb-0.5">MÁX M²</span>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={formatWithCommas(filters.buildUpSizeMax)}
                                        onChange={e => {
                                            const parsed = parseCommas(e.target.value);
                                            if (/^\d*\.?\d*$/.test(parsed)) {
                                                setFilters(prev => ({ ...prev, buildUpSizeMax: parsed }));
                                            }
                                        }}
                                        className={clsx(
                                            "bg-transparent border-none p-0 w-full outline-none text-sm font-bold",
                                            isLight ? "text-slate-950" : "text-white"
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Terreno</label>
                            <div className="space-y-2">
                                <div className={clsx("relative rounded-2xl border px-4 py-2", isLight ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/5")}>
                                    <span className="text-[9px] font-bold text-slate-400 block mb-0.5">MÍN V²</span>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={formatWithCommas(filters.landSizeMin)}
                                        onChange={e => {
                                            const parsed = parseCommas(e.target.value);
                                            if (/^\d*\.?\d*$/.test(parsed)) {
                                                setFilters(prev => ({ ...prev, landSizeMin: parsed }));
                                            }
                                        }}
                                        className={clsx(
                                            "bg-transparent border-none p-0 w-full outline-none text-sm font-bold",
                                            isLight ? "text-slate-950" : "text-white"
                                        )}
                                    />
                                </div>
                                <div className={clsx("relative rounded-2xl border px-4 py-2", isLight ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/5")}>
                                    <span className="text-[9px] font-bold text-slate-400 block mb-0.5">MÁX V²</span>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={formatWithCommas(filters.landSizeMax)}
                                        onChange={e => {
                                            const parsed = parseCommas(e.target.value);
                                            if (/^\d*\.?\d*$/.test(parsed)) {
                                                setFilters(prev => ({ ...prev, landSizeMax: parsed }));
                                            }
                                        }}
                                        className={clsx(
                                            "bg-transparent border-none p-0 w-full outline-none text-sm font-bold",
                                            isLight ? "text-slate-950" : "text-white"
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Amenities Tag Cloud */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Amenidades Clave</label>
                        <div className="flex flex-wrap gap-2">
                            {dbAmenities.map(amenity => {
                                const selected = (filters.amenities || []).includes(amenity);
                                return (
                                    <button
                                        key={amenity}
                                        onClick={() => toggleAmenity(amenity)}
                                        className={clsx(
                                            "px-4 py-2 rounded-xl text-[11px] font-bold border transition-all duration-300",
                                            selected
                                                ? isLight
                                                    ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100"
                                                    : "bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/20"
                                                : isLight
                                                    ? "bg-white border-slate-100 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                                                    : "bg-white/5 border-white/5 text-slate-400 hover:bg-white/10 hover:border-white/20"
                                        )}
                                    >
                                        {amenity}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Agente — agents in the current user's agency. Hidden for
                        users restricted to their own properties (the picker would
                        be a no-op since the query is forced to their own id). */}
                    {showAgentFilter && (
                    <div className="space-y-4">
                        <div className="flex items-baseline justify-between pl-1">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Agente</label>
                            {(filters.agent_ids?.length ?? 0) > 0 && (
                                <button
                                    onClick={() => setFilters(prev => ({ ...prev, agent_ids: [] }))}
                                    className="text-[10px] font-bold text-slate-400 hover:text-rose-500 uppercase"
                                >
                                    Limpiar
                                </button>
                            )}
                        </div>
                        <MultiSelectDropdown<string>
                            theme={theme}
                            options={agents}
                            selectedIds={filters.agent_ids ?? []}
                            onChange={(ids) => setFilters(prev => ({ ...prev, agent_ids: ids }))}
                            placeholder="Selecciona uno o más agentes"
                            emptyMessage={agencyId ? "Cargando agentes…" : "Inicia sesión para ver agentes"}
                        />
                    </div>
                    )}

                    {/* Proyecto — developments the agency has created */}
                    <div className="space-y-4">
                        <div className="flex items-baseline justify-between pl-1">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Proyecto</label>
                            {(filters.proyecto_ids?.length ?? 0) > 0 && (
                                <button
                                    onClick={() => setFilters(prev => ({ ...prev, proyecto_ids: [] }))}
                                    className="text-[10px] font-bold text-slate-400 hover:text-rose-500 uppercase"
                                >
                                    Limpiar
                                </button>
                            )}
                        </div>
                        <MultiSelectDropdown<string>
                            theme={theme}
                            options={proyectos}
                            selectedIds={filters.proyecto_ids ?? []}
                            onChange={(ids) => setFilters(prev => ({ ...prev, proyecto_ids: ids }))}
                            placeholder="Selecciona uno o más proyectos"
                            emptyMessage={agencyId ? "Aún no tienes proyectos" : "Inicia sesión para ver proyectos"}
                        />
                    </div>

                    {/* Network & Visibility */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">Visibilidad & Red</label>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <p className="text-[10px] font-bold text-slate-400 pl-1 uppercase">Portal</p>
                                <select
                                    value={filters.publishStatus || 'All'}
                                    onChange={e => setFilters(prev => ({ ...prev, publishStatus: e.target.value as any }))}
                                    className={clsx(
                                        "w-full px-4 py-3 rounded-2xl border text-xs font-black transition-all appearance-none outline-none cursor-pointer",
                                        isLight ? "bg-slate-50 border-slate-200 focus:bg-white text-slate-950" : "bg-white/5 border-white/5 text-white focus:bg-white/10"
                                    )}
                                >
                                    <option value="All">CUALQUIERA</option>
                                    <option value="published">PUBLICADA</option>
                                    <option value="unpublished">NO PUBLICADA</option>
                                    <option value="expired">EXPIRANDO</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <p className="text-[10px] font-bold text-slate-400 pl-1 uppercase">Intercambio</p>
                                <select
                                    value={filters.shareStatus || 'All'}
                                    onChange={e => setFilters(prev => ({ ...prev, shareStatus: e.target.value as any }))}
                                    className={clsx(
                                        "w-full px-4 py-3 rounded-2xl border text-xs font-black transition-all appearance-none outline-none cursor-pointer",
                                        isLight ? "bg-slate-50 border-slate-200 focus:bg-white text-slate-950" : "bg-white/5 border-white/5 text-white focus:bg-white/10"
                                    )}
                                >
                                    <option value="All">CUALQUIERA</option>
                                    <option value="shared">COMPARTIDA</option>
                                    <option value="expiring">POR VENCER</option>
                                    <option value="not_shared">PRIVADO</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sticky Footer */}
                <div className={clsx(
                    "p-8 border-t flex items-center justify-between gap-4",
                    isLight ? "bg-slate-50/80 border-slate-100" : "bg-slate-900/80 border-white/5"
                )}>
                    <button
                        onClick={handleReset}
                        className="text-xs font-black text-slate-500 hover:text-rose-500 uppercase tracking-widest transition-colors flex items-center gap-2"
                    >
                        <X size={14} /> Limpiar
                    </button>

                    <button
                        onClick={handleApply}
                        className="flex-1 py-4 px-6 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-black tracking-widest uppercase shadow-[0_10px_30px_rgba(79,70,229,0.4)] transition-all active:scale-95 flex items-center justify-center gap-2"
                    >
                        Aplicar Filtros
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};
